import { useNavigate, useSubmit } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../Components/functions/Api";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  getIdFromHash,
  getProfileFromWallet,
  truncateWalletAddress,
  getMyTransactions,
} from "../../../Components/functions/Functions";
import { Logo, NUSDIcon } from "../../../AssetComponents/Images";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { PropertyImageWithFallback } from "../../../Components/Images/PropertyImageWithFallback";
import {
  removeItem,
  removeTxByHash,
} from "../../../ReduxSlices/NotificationSlice";
import { BlackButton } from "../../../Components/Buttons/BlackButton";
import Modal from "react-responsive-modal";
import {
  SelectionGroup,
  SelectionItem,
} from "../../../Components/Selection/Selection";
import { LightIcon } from "../../../AssetComponents/Images";
import { NumericFormat } from "react-number-format";
import generatePDF from "react-to-pdf";
import ReactToPrint from "react-to-print";
import { Fade } from "react-awesome-reveal";
import { toast } from "react-toastify";

const openExplorerLink = (txHash) => {
  const explorerUrl = `https://nibiru.explorers.guru/transaction/${txHash}`;
  window.open(explorerUrl, '_blank');
};

export const Transactions = ({ mode }) => {
  const account = useSelector((state) => state.auth.account);
  const nfts = useSelector((state) => state.nft.nfts);
  const diff = useSelector((state) => state.time.diffToUTC);
  const notificationContent = useSelector((state) => state.notification.items);
  const profile = useSelector((state) => state.auth.profile);
  const dispatch = useDispatch();
  const invoiceRef = useRef();
  const [txes, setTxes] = useState([]);
  const [ids, setIds] = useState();
  const [parsedString, setParsedString] = useState();
  const [detailModal, setDetailModal] = useState();
  const [flag, setFlag] = useState(0);
  const [senderName, setSenderName] = useState();
  const [isLoading, setIsLoading] = useState(true);
  // 🚀 OPTIMIZED: Use optimized backend endpoint (pre-filtered, paginated, no client-side filtering)
  const getTxes = useCallback(async (counts = null) => {
    if (!account) return;
    
    setIsLoading(true);
    try {
      // Map flag to action type: 0=all, 1=completed, 2=pending, 3=cancelled
      const actionMap = {
        0: null, // all
        1: "finalize_short_term_rental", // completed
        2: "set_reservation_for_short_term", // pending
        3: "cancelled", // cancelled (special filter for multiple action types)
      };
      
      const action = actionMap[flag];
      
      // 🚀 OPTIMIZED: Use new optimized endpoint - backend handles all filtering
      const transactions = await getMyTransactions(account, mode, action, 1, counts || 100);
      
      // Transactions are already sorted and filtered by backend
      setTxes(transactions || []);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTxes([]);
      setIsLoading(false);
    }
  }, [account, mode, flag]);

  const filterNotifications = () => {
    for (let i = 0; i < txes.length; i++) {
      dispatch(removeTxByHash(txes[i].txHash));
    }
  };

  // 🚀 OPTIMIZED: Fetch when account or flag changes
  useEffect(() => {
    if (account) getTxes(null);
  }, [account, flag, getTxes]);

  useEffect(() => {
    filterNotifications();
  }, [notificationContent, txes]);

  // 🚀 REAL-TIME UPDATES: Listen to socket events for instant transaction updates
  useEffect(() => {
    if (!account) return;

    const handleTransactionAdded = (event) => {
      const data = event.detail;
      console.log('📋 New transaction detected:', data);
      
      // Check if this transaction involves the current user
      const normalizedAccount = account?.toLowerCase();
      const normalizedSender = data?.sender?.toLowerCase();
      const normalizedReceiver = data?.receiver?.toLowerCase();
      
      if (normalizedSender === normalizedAccount || normalizedReceiver === normalizedAccount) {
        console.log('🔄 Refreshing transactions list...');
        
        // Get action description for toast
        const getActionDescription = (action) => {
          switch (action) {
            case "set_reservation_for_short_term":
              return "New reservation";
            case "finalize_short_term_rental":
              return "Rental completed";
            case "cancel_reservation_for_shortterm":
              return "Reservation cancelled";
            case "cancel_rental_for_shortterm":
              return "Rental cancelled";
            case "reject_reservation_for_shortterm":
              return "Reservation rejected";
            default:
              return "New transaction";
          }
        };
        
        // Show toast notification
        toast.info(`📋 ${getActionDescription(data.action)} - Transaction list updated`, {
          autoClose: 2000,
          position: "bottom-right",
        });
        
        // Refresh transactions list
        getTxes(null);
      }
    };

    // Listen to transaction events (emitted from App.js when socket receives "event")
    window.addEventListener('transaction:added', handleTransactionAdded);

    return () => {
      window.removeEventListener('transaction:added', handleTransactionAdded);
    };
  }, [account, getTxes]);

  const parseAction = (action) => {
    switch (action) {
      case "finalize_short_term_rental":
        return (
          <div className="flex items-center">
            <svg
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_597_40833)">
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#38A569"
                />
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_597_40833)"
                  fill-opacity="0.18"
                />
                <rect
                  x="9.03255"
                  y="8.83333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M14.9922 17.0834L17.0755 19.1667L20.8255 15.4167"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_597_40833"
                  x="0.699218"
                  y="0.499999"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_597_40833"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_597_40833"
                    result="effect2_dropShadow_597_40833"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_597_40833"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_597_40833"
                  x1="17.6992"
                  y1="8.5"
                  x2="17.6992"
                  y2="20.125"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[#38A569]">Completed</div>
          </div>
        );
        break;
      case "set_reservation_for_short_term":
        return (
          <div className="flex items-center">
            <svg
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_597_40833)">
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#38A569"
                />
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_597_40833)"
                  fill-opacity="0.18"
                />
                <rect
                  x="9.03255"
                  y="8.83333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M14.9922 17.0834L17.0755 19.1667L20.8255 15.4167"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_597_40833"
                  x="0.699218"
                  y="0.499999"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_597_40833"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_597_40833"
                    result="effect2_dropShadow_597_40833"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_597_40833"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_597_40833"
                  x1="17.6992"
                  y1="8.5"
                  x2="17.6992"
                  y2="20.125"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[#38A569]">Reserved</div>
          </div>
        );
        break;
      case "cancel_reservation_for_shortterm":
        return (
          <div className="flex items-center">
            <svg
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_597_40913)">
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#DB1F22"
                />
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_597_40913)"
                  fill-opacity="0.18"
                />
                <rect
                  x="9.03255"
                  y="8.83333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M15.8242 15.625L19.5742 19.375"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M15.8242 19.375L19.5742 15.625"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_597_40913"
                  x="0.699218"
                  y="0.499999"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_597_40913"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_597_40913"
                    result="effect2_dropShadow_597_40913"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_597_40913"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_597_40913"
                  x1="17.6992"
                  y1="8.5"
                  x2="17.6992"
                  y2="20.125"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[#DB1F22]">Canceled</div>
          </div>
        );
        break;
      case "cancel_rental_for_shortterm":
        return (
          <div className="flex items-center">
            <svg
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_597_40913)">
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#DB1F22"
                />
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_597_40913)"
                  fill-opacity="0.18"
                />
                <rect
                  x="9.03255"
                  y="8.83333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M15.8242 15.625L19.5742 19.375"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M15.8242 19.375L19.5742 15.625"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_597_40913"
                  x="0.699218"
                  y="0.499999"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_597_40913"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_597_40913"
                    result="effect2_dropShadow_597_40913"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_597_40913"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_597_40913"
                  x1="17.6992"
                  y1="8.5"
                  x2="17.6992"
                  y2="20.125"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[#DB1F22]">Canceled</div>
          </div>
        );
        break;
      case "reject_reservation_for_shortterm":
        return (
          <div className="flex items-center">
            <svg
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_597_40913)">
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#DB1F22"
                />
                <rect
                  x="8.69922"
                  y="8.5"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_597_40913)"
                  fill-opacity="0.18"
                />
                <rect
                  x="9.03255"
                  y="8.83333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M15.8242 15.625L19.5742 19.375"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M15.8242 19.375L19.5742 15.625"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_597_40913"
                  x="0.699218"
                  y="0.499999"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_597_40913"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_597_40913"
                    result="effect2_dropShadow_597_40913"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_597_40913"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_597_40913"
                  x1="17.6992"
                  y1="8.5"
                  x2="17.6992"
                  y2="20.125"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[#DB1F22]">Rejected</div>
          </div>
        );
        break;
      default:
        break;
    }
  };

  const parseString = async (tx) => {
    if (!tx) return;
    let value = "";
    if (tx.sender === account) {
      value += "You";
      setSenderName(profile?.Name?.replace("/", " "));
    } else {
      // if (nfts[tx.token_id]?.access.owner === tx.sender) value += "Host ";
      // else value += "Traveler ";
      const sender = await getProfileFromWallet(tx.sender);
      value += sender.Name?.replace("/", " ");
      setSenderName(sender?.Name?.replace("/", " "));
    }
    value += " have ";

    switch (tx.action) {
      case "finalize_short_term_rental":
        value += "completed ";
        break;
      case "set_reservation_for_short_term":
        value += "successfully paid ";
        break;
      case "cancel_reservation_for_shortterm":
        value += "canceled ";
        break;
      case "cancel_rental_for_shortterm":
        value += "canceled ";
        break;
      case "reject_reservation_for_shortterm":
        value += "rejected ";
      default:
        break;
    }
    value += "for Order ";
    value += (tx?.id || "").toUpperCase().slice(-10);
    value += ". ";
    value += "This is a receipt from Coded Estate";
    setParsedString(value);
  };

  useEffect(() => {
    if (ids) {
      parseString(ids);
      setDetailModal(true);
    } else setDetailModal(false);
  }, [ids]);

  const downloadPdf = () => {
    const getTargetElement = () => document.getElementById("invoice");
    // console.log(element);
    generatePDF(getTargetElement, {
      filename: "CodedEstate_Invoice.pdf",
      page: {
        margin: 20,
      },
    });
  };

  const formatDate = (timeUTC, diff) => {
    try {
      const timestamp = (Math.round(new Date(timeUTC).getTime() / 1000) - diff) * 1000;
      if (isNaN(timestamp)) {
        return 'Invalid date';
      }
      return new Intl.DateTimeFormat("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(timestamp);
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  const formatTime = (timeUTC, diff) => {
    try {
      const timestamp = (Math.round(new Date(timeUTC).getTime() / 1000) - diff) * 1000;
      if (isNaN(timestamp)) {
        return 'Invalid time';
      }
      return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(timestamp);
    } catch (error) {
      return 'Invalid time';
    }
  };

  return (
    <div className="w-full overflow-auto max-h-[calc(100vh-80px)] h-full p-[8px] sm:p-[10px]">
      <Modal
        open={detailModal}
        onClose={() => setIds(null)}
        center
        classNames={{
          modal:
            "w-[90vw] max-w-[500px] p-0 h-max rounded-[8px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]",
        }}
      >
        <div className="w-full">
          <div
            className="flex flex-col items-center space-y-2 p-4"
            id="invoice"
            ref={invoiceRef}
          >
            <div className="scale-75 sm:scale-100">
              <Logo className="w-[120px]" />
            </div>
            <div className="w-full text-sm sm:text-base">
              Hello{" "}
              <span className="font-semibold">
                {profile?.Name?.replace("/", " ")}
              </span>
            </div>
            <div className="w-full text-sm sm:text-base">{parsedString}</div>
            <div className="w-full font-semibold text-[18px] sm:text-[20px] text-[#5b1dee]">
              <NumericFormat
                value={ids?.funds?.amount || 0}
                thousandSeparator
                displayType="text"
              />
              <span className="ml-2">USDC</span>
            </div>
            <div className="w-full text-sm sm:text-base">
              {ids &&
                new Intl.DateTimeFormat("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                }).format(
                  (Math.round(new Date(ids?.timeUTC).getTime() / 1000) - diff) *
                    1000
                )}
            </div>
            <div className="w-full flex justify-between text-sm sm:text-base">
              <div>Confirmation Code</div>
              <div className="font-semibold">
                {(ids?.id || "").toUpperCase().slice(-10)}
              </div>
            </div>

            {ids?.funds?.type ? (
              <>
                <div className="w-full space-y-1">
                  <div className="font-bold text-sm sm:text-base">FROM</div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Address</div>
                    <div className="text-[#737373] truncate ml-2">{ids?.sender}</div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>ID</div>
                    <div className="text-[#737373] truncate ml-2">{senderName}</div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Token</div>
                    <div className="text-[#737373]">USDC</div>
                  </div>
                </div>
                <div className="w-full h-[1px] border-dashed border-[1px]" />
                <div className="w-full space-y-1">
                  <div className="font-bold text-sm sm:text-base">TO</div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Contract address</div>
                    <div className="text-[#737373] truncate ml-2">
                      {truncateWalletAddress(
                        process.env.REACT_APP_RENTAL_SMART_CONTRACT
                      )}
                    </div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>ID</div>
                    <div className="text-[#737373]">Coded Estate</div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Token</div>
                    <div className="text-[#737373]">USDC</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="w-full space-y-1">
                  <div className="font-bold text-sm sm:text-base">FROM</div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Contract address</div>
                    <div className="text-[#737373] truncate ml-2">
                      {truncateWalletAddress(
                        process.env.REACT_APP_RENTAL_SMART_CONTRACT
                      )}
                    </div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>ID</div>
                    <div className="text-[#737373]">Coded Estate</div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Token</div>
                    <div className="text-[#737373]">USDC</div>
                  </div>
                </div>
                <div className="w-full h-[1px] border-dashed border-[1px]" />
                <div className="w-full space-y-1">
                  <div className="font-bold text-sm sm:text-base">TO</div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Address</div>
                    <div className="text-[#737373] truncate ml-2">{ids?.sender}</div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>ID</div>
                    <div className="text-[#737373] truncate ml-2">{senderName}</div>
                  </div>
                  <div className="w-full flex justify-between text-xs sm:text-sm">
                    <div>Token</div>
                    <div className="text-[#737373]">USDC</div>
                  </div>
                </div>
              </>
            )}

            <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2">
              <div className="flex items-center gap-2">
                <PropertyImageWithFallback
                  src={nfts[ids?.token_id]?.metaData.images[0]}
                  alt="Property"
                  className="w-[80px] sm:w-[100px] h-[60px] sm:h-[70px] rounded-[4px]"
                />
                <div className="">
                  <div className="font-semibold text-[16px] sm:text-[18px]">
                    {nfts[ids?.token_id]?.metaData?.buildingName || "Property"}
                  </div>
                  <div className="font-normal text-sm sm:text-base">
                    {(ids?.id || "").toUpperCase().slice(-10)}
                  </div>
                </div>
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <div className="flex items-center gap-2 justify-end">
                  <div className="scale-75 sm:scale-100">
                    <NUSDIcon />
                  </div>
                  <div className="font-normal text-sm sm:text-base">
                    <span
                      className={
                        ids?.funds?.type ? "text-[#38A569]" : "text-[#DB1F22]"
                      }
                    >
                      {ids?.funds?.amount || 0}
                    </span>{" "}
                    USDC
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full">
              <div className="w-full flex items-center justify-between text-sm sm:text-base">
                <div className="">
                  {nfts[ids?.token_id]?.metaData?.buildingName || "Property"}
                </div>
                <div className="">
                  <NumericFormat
                    value={ids?.funds?.amount || 0}
                    thousandSeparator
                    displayType="text"
                  />
                  <span className="ml-2">USDC</span>
                </div>
              </div>
              <div className="w-full flex items-center justify-between text-sm sm:text-base">
                <div className="">Total</div>
                <div className="text-[#5b1dee]">
                  <NumericFormat
                    value={ids?.funds?.amount || 0}
                    thousandSeparator
                    displayType="text"
                  />
                  <span className="ml-2">USDC</span>
                </div>
              </div>
            </div>
          </div>
          <div className="w-[96%] mx-auto grid grid-cols-1 sm:grid-cols-2 gap-2 my-2">
            <PurpleButton
              text={
                <div className="flex items-center w-full justify-center gap-2 text-sm sm:text-base">
                  <svg
                    className="scale-75 sm:scale-100"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3 16V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V16C21 15.4477 20.5523 15 20 15H16.6175C16.2388 15 15.8925 15.214 15.7231 15.5528L15.2759 16.4472C15.1065 16.786 14.7602 17 14.3815 17H9.61753C9.2386 17 8.89222 16.7858 8.72291 16.4468L8.27659 15.5532C8.10728 15.2142 7.76092 15 7.382 15H4C3.44772 15 3 15.4477 3 16Z"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 10L12 12L14 10"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 12V7"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 12V5C20 3.89543 19.1046 3 18 3H6C4.89543 3 4 3.89543 4 5V12"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div>Download Invoice</div>
                </div>
              }
              onClick={() => downloadPdf()}
            />
            <ReactToPrint
              trigger={() => {
                return (
                  <BlackButton
                    text={
                      <div className="flex items-center w-full justify-center gap-2 text-sm sm:text-base">
                        <svg
                          className="scale-75 sm:scale-100"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 8V4C7 3.448 7.448 3 8 3H16C16.552 3 17 3.448 17 4V8"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 17H5C3.895 17 3 16.105 3 15V10C3 8.895 3.895 8 5 8H19C20.105 8 21 8.895 21 10V15C21 16.105 20.105 17 19 17H17"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7 13.7998H17V19.9998C17 20.5518 16.552 20.9998 16 20.9998H8C7.448 20.9998 7 20.5518 7 19.9998V13.7998Z"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 11H8"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <div>Print Invoice</div>
                      </div>
                    }
                  />
                );
              }}
              content={() => invoiceRef.current}
            />
          </div>
        </div>
      </Modal>
      
      <div className="w-full h-full space-y-[16px] sm:space-y-[20px] rounded-[10px] p-[4px] sm:p-[8px] flex flex-col">
        <div className="bg-white border-[2px] h-max border-[#E3E3E3] p-[12px] sm:p-[16px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
          <div className="flex items-center gap-[8px] sm:gap-[10px]">
            <div className="scale-75 sm:scale-100">
              <svg
                width="62"
                height="62"
                viewBox="0 0 62 62"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g filter="url(#filter0_dd_597_41881)">
                  <rect
                    x="11"
                    y="11"
                    width="40"
                    height="40"
                    rx="20"
                    fill="#5B1DEE"
                  />
                  <rect
                    x="11"
                    y="11"
                    width="40"
                    height="40"
                    rx="20"
                    fill="url(#paint0_linear_597_41881)"
                    fillOpacity="0.18"
                  />
                  <g filter="url(#filter1_d_597_41881)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M39 38H23C21.895 38 21 37.105 21 36V26C21 24.895 21.895 24 23 24H39C40.105 24 41 24.895 41 26V36C41 37.105 40.105 38 39 38Z"
                      fill="white"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M39 38H23C21.895 38 21 37.105 21 36V26C21 24.895 21.895 24 23 24H39C40.105 24 41 24.895 41 26V36C41 37.105 40.105 38 39 38Z"
                      stroke="#202020"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <path
                    d="M24 29V27H26"
                    stroke="#202020"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M24 33V35H26"
                    stroke="#202020"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M36 27H38V29"
                    stroke="#202020"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M36 35H38V33"
                    stroke="#202020"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M32.7678 29.2322C33.7441 30.2085 33.7441 31.7914 32.7678 32.7677C31.7915 33.744 30.2085 33.744 29.2322 32.7677C28.2559 31.7914 28.2559 30.2085 29.2322 29.2322C30.2085 28.2559 31.7915 28.2559 32.7678 29.2322"
                    stroke="#202020"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_dd_597_41881"
                    x="0.333331"
                    y="0.333331"
                    width="61.3333"
                    height="61.3333"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="2.66667" dy="2.66667" />
                    <feGaussianBlur stdDeviation="4" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_597_41881"
                    />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="-2.66667" dy="-2.66667" />
                    <feGaussianBlur stdDeviation="4" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="effect1_dropShadow_597_41881"
                      result="effect2_dropShadow_597_41881"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect2_dropShadow_597_41881"
                      result="shape"
                    />
                  </filter>
                  <filter
                    id="filter1_d_597_41881"
                    x="20.25"
                    y="23.25"
                    width="22.5"
                    height="16.5"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="1" dy="1" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_597_41881"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_597_41881"
                      result="shape"
                    />
                  </filter>
                  <linearGradient
                    id="paint0_linear_597_41881"
                    x1="31"
                    y1="11"
                    x2="31"
                    y2="36.8333"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div>
              <div className="font-semibold text-[20px] sm:text-[24px]">Transactions</div>
              <div className="text-[#666666] font-normal text-sm sm:text-base">
                Your real estate NFT transactions
              </div>
            </div>
          </div>
        </div>
        
        <div className="w-full justify-between flex flex-col sm:flex-row items-start sm:items-center gap-[12px] sm:gap-0">
          <SelectionGroup
            defaultItem={flag}
            className="border-[2px] border-[#e3e3e3] w-full sm:w-max px-[4px] sm:px-[6px] py-[4px] gap-[4px] sm:gap-[8px] flex items-center rounded-[14px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] overflow-x-auto"
          >
            <SelectionItem
              SelectedItem={
                <div className="py-[4px] cursor-pointer bg-white rounded-[10px] min-w-[60px] sm:w-[70px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-sm">All</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(0)}
                  className="py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] min-w-[60px] sm:w-[70px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-sm">All</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[4px] cursor-pointer bg-white rounded-[10px] min-w-[100px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-sm">Completed</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(1)}
                  className="py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] min-w-[100px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-sm">Completed</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[4px] cursor-pointer bg-white rounded-[10px] min-w-[80px] sm:w-[100px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-sm">Pending</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(2)}
                  className="py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] min-w-[80px] sm:w-[100px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-sm">Pending</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[4px] cursor-pointer bg-white rounded-[10px] min-w-[90px] sm:w-[110px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-sm">Canceled</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(3)}
                  className="py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] min-w-[90px] sm:w-[110px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-sm">Canceled</div>
                </div>
              }
            />
          </SelectionGroup>

          <div className="flex gap-[8px] sm:gap-[10px] items-center border-[#E3E3E3] border-[1px] rounded-[12px] pl-[12px] sm:pl-[16px] pr-[6px] sm:pr-[8px] py-[8px] sm:py-[9px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full sm:w-auto">
            <svg
              className="scale-75 sm:scale-100"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.25 12.25L8.75 8.75M1.75 5.83333C1.75 6.36956 1.85562 6.90054 2.06083 7.39596C2.26603 7.89137 2.56681 8.34151 2.94598 8.72069C3.32515 9.09986 3.7753 9.40063 4.27071 9.60584C4.76612 9.81105 5.2971 9.91667 5.83333 9.91667C6.36956 9.91667 6.90054 9.81105 7.39596 9.60584C7.89137 9.40063 8.34151 9.09986 8.72069 8.72069C9.09986 8.34151 9.40063 7.89137 9.60584 7.39596C9.81105 6.90054 9.91667 6.36956 9.91667 5.83333C9.91667 5.2971 9.81105 4.76612 9.60584 4.27071C9.40063 3.7753 9.09986 3.32515 8.72069 2.94598C8.34151 2.56681 7.89137 2.26603 7.39596 2.06083C6.90054 1.85562 6.36956 1.75 5.83333 1.75C5.2971 1.75 4.76612 1.85562 4.27071 2.06083C3.7753 2.26603 3.32515 2.56681 2.94598 2.94598C2.56681 3.32515 2.26603 3.7753 2.06083 4.27071C1.85562 4.76612 1.75 5.2971 1.75 5.83333Z"
                stroke="#202020"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <input
              placeholder="Search"
              className="w-full outline-none text-sm sm:text-base"
            />
            <div className="scale-75 sm:scale-100">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="20" height="20" rx="4" fill="#E3E3E3" />
                <path
                  d="M11.4837 6.38636L9.13991 15.0938H8.19176L10.5355 6.38636H11.4837Z"
                  fill="#202020"
                />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="w-full bg-[#fafafa] max-h-[calc(100vh-200px)] scrollbarwidth overflow-auto border-[#E3E3E3] pb-4 rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
          {isLoading ? (
            <div className="w-full h-[calc(100vh-340px)] flex flex-col items-center justify-center px-4">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b1dee]"></div>
                <div className="font-semibold text-sm sm:text-base text-center">Transactions Loading...</div>
                <div className="text-[#909090] text-xs sm:text-sm text-center">Please wait while we fetch your transactions</div>
              </div>
            </div>
          ) : txes.length === 0 ? (
            <div className="w-full h-[calc(100vh-340px)] flex flex-col items-center justify-center px-4">
              <div className="scale-75 sm:scale-100">
                <svg
                  width="88"
                  height="89"
                  viewBox="0 0 88 89"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_dd_559_60597)">
                    <rect
                      x="8"
                      y="8.5"
                      width="72"
                      height="72"
                      rx="36"
                      fill="white"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M25.3346 35.1667H62.668C65.2463 35.1667 67.3346 37.255 67.3346 39.8333V63.1667C67.3346 65.745 65.2463 67.8333 62.668 67.8333H25.3346C22.7563 67.8333 20.668 65.745 20.668 63.1667V39.8333C20.668 37.255 22.7563 35.1667 25.3346 35.1667Z"
                      stroke="#531AD9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M48.1261 47.3751C50.4041 49.6532 50.4041 53.3466 48.1261 55.6247C45.848 57.9028 42.1546 57.9028 39.8765 55.6247C37.5985 53.3466 37.5985 49.6532 39.8765 47.3751C42.1546 45.0971 45.848 45.0971 48.1261 47.3751"
                      stroke="#531AD9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M57.9987 57.3333V45.6667"
                      stroke="#531AD9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M29.9987 57.3333V45.6667"
                      stroke="#531AD9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M62.6654 28.1667H25.332"
                      stroke="#531AD9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M60.3346 21.1667H27.668"
                      stroke="#531AD9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_dd_559_60597"
                      x="-9.53674e-07"
                      y="0.499999"
                      width="88"
                      height="88"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dx="-2" dy="-2" />
                      <feGaussianBlur stdDeviation="3" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_559_60597"
                      />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dx="2" dy="2" />
                      <feGaussianBlur stdDeviation="3" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="effect1_dropShadow_559_60597"
                        result="effect2_dropShadow_559_60597"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect2_dropShadow_559_60597"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>

              <div className="font-semibold text-sm sm:text-base text-center">No Transaction yet</div>
              <div className="text-[#909090] text-xs sm:text-sm text-center">Not transaction yet for now</div>
            </div>
          ) : (
            <Fade>
              {/* Mobile Card View */}
              <div className="block lg:hidden space-y-3 p-3">
                {txes?.map((tx, i) => {
                  // 🚀 OPTIMIZED: Transactions are already filtered by backend, just render them
                  return (
                      <div
                        key={i}
                        className="bg-white rounded-[8px] p-4 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <img
                            alt=""
                            src={nfts[tx.token_id]?.metaData.images[0]}
                            className="w-[50px] h-[50px] rounded-[4px] object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">
                              {nfts[tx.token_id]?.metaData?.buildingName || "Property"}
                            </div>
                            <div className="text-xs text-[#666666]">
                              ID: {tx.token_id}
                            </div>
                            <div className="text-xs text-[#666666]">
                              {(tx.id || "").toUpperCase().slice(-10)}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 mb-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-[#666666]">Date:</span>
                            <div className="text-right">
                              <div className="text-xs">{formatDate(tx.timeUTC, diff)}</div>
                              <div className="text-xs text-[#666666]">
                                At {formatTime(tx.timeUTC, diff)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-[#666666]">Amount:</span>
                            <div className="flex items-center gap-1">
                              <div className="scale-75">
                                <NUSDIcon />
                              </div>
                              <span
                                className={`text-sm ${
                                  tx?.funds?.type
                                    ? "text-[#38A569]"
                                    : "text-[#DB1F22]"
                                }`}
                              >
                                {tx?.funds?.amount || 0}
                              </span>
                              <span className="text-xs">USDC</span>
                            </div>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-xs text-[#666666]">Tx Hash:</span>
                            <div 
                              className="text-[#5b1dee] cursor-pointer hover:underline text-xs"
                              onClick={() => openExplorerLink(tx.txHash)}
                              title="View on Nibiru Explorer"
                            >
                              {truncateWalletAddress(tx.txHash)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="shadow-md rounded-[4px] pr-[6px] border-[1px] w-max scale-75">
                            {parseAction(tx.action)}
                          </div>
                          <div className="scale-75">
                            <PurpleButton
                              text="Details"
                              onClick={() => setIds(tx)}
                            />
                          </div>
                        </div>
                      </div>
                    );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="w-full border-spacing-y-1 border-separate">
                  <thead>
                    <tr className="w-full h-[40px]">
                      <th className="pl-3 text-left text-sm sm:text-base">Property</th>
                      <th className="text-left text-sm sm:text-base">Date</th>
                      <th className="text-left text-sm sm:text-base">ID</th>
                      <th className="text-left text-sm sm:text-base">Amount</th>
                      <th className="text-left text-sm sm:text-base">Status</th>
                      <th className="text-left text-sm sm:text-base">Tx Hash</th>
                      <th className="text-left text-sm sm:text-base">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txes?.map((tx, i) => {
                      // 🚀 OPTIMIZED: Transactions are already filtered by backend, just render them
                      return (
                          <tr
                            key={i}
                            className={
                              i % 2 === 0
                                ? "h-[60px] bg-[#FFFFFF] hover:shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full"
                                : "h-[60px] bg-[#FAFAFA] hover:shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full"
                            }
                          >
                            <td>
                              <div className="pl-3 flex items-center gap-[10px]">
                                <img
                                  alt=""
                                  src={nfts[tx.token_id]?.metaData.images[0]}
                                  className="w-[30px] h-[30px] rounded-[4px] object-cover"
                                />
                                <div>
                                  <div className="font-normal text-sm">
                                    {nfts[tx.token_id]?.metaData?.buildingName || "Property"}
                                  </div>
                                  <div className="text-[12px] text-[#666666]">
                                    ID: {tx.token_id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="font-normal text-sm">
                                  {formatDate(tx.timeUTC, diff)}
                                </div>
                                <div className="text-[12px] text-[#666666]">
                                  At {formatTime(tx.timeUTC, diff)}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="font-normal text-sm">
                                {(tx.id || "").toUpperCase().slice(-10)}
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <div className="scale-75 sm:scale-100">
                                  <NUSDIcon />
                                </div>
                                <div className="font-normal text-sm">
                                  <span
                                    className={
                                      tx?.funds?.type
                                        ? "text-[#38A569]"
                                        : "text-[#DB1F22]"
                                    }
                                  >
                                    {tx?.funds?.amount || 0}
                                  </span>{" "}
                                  USDC
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="shadow-md rounded-[4px] pr-[6px] sm:pr-[10px] border-[1px] w-max scale-75 sm:scale-100">
                                {parseAction(tx.action)}
                              </div>
                            </td>
                            <td>
                              <div 
                                className="text-[#5b1dee] cursor-pointer hover:underline text-sm"
                                onClick={() => openExplorerLink(tx.txHash)}
                                title="View on Nibiru Explorer"
                              >
                                {truncateWalletAddress(tx.txHash)}
                              </div>
                            </td>
                            <td>
                              <div className="w-max scale-75 sm:scale-100">
                                <PurpleButton
                                  text="Details"
                                  onClick={() => setIds(tx)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            </Fade>
          )}
        </div>
      </div>
    </div>
  );
};
